import React, { useEffect, useState } from "react"
import { get, post } from "../../utils/apiClient"
import toast from "react-hot-toast"
import Select from "react-select"
import { customSelectStyles, displayNetIdEmail } from "../../utils/common"

export default function QueuePermissionModal({
  onClose,
  onSaved,
  editData,
  queuesForSelect,
  authUser,
}) {
  const [netIds, setNetIds] = useState([])
  const [queues, setQueues] = useState([])
  const [roles, setRoles] = useState([])
  const [templates, setTemplates] = useState({})
  const [defaultRoleWisePermissions, setDefaultRoleWisePermissions] = useState(
    []
  )

  const [selectedNetId, setSelectedNetId] = useState("")
  const [selectedQueue, setSelectedQueue] = useState("")
  const [selectedRole, setSelectedRole] = useState("")
  const [rolePermissions, setRolePermissions] = useState({})
  const [initialPermissions, setInitialPermissions] = useState({}) // used for reload default

  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)

  useEffect(() => {
    const loadDetails = async () => {
      setLoading(true)
      try {
        const data = await get("/manage/resources/details")

        // set NetIds and Queues as per permissions
        const nIds = editData ? [editData] : data.netIds

        const ques = editData
          ? [editData.queue]
          : authUser.loggedInUserType === "IT"
          ? data.queues
          : queuesForSelect

        setNetIds(nIds || [])
        setQueues(ques || [])
        setRoles(data.roles || [])
        setTemplates(data.globalPermissionTemplate || {})
        setDefaultRoleWisePermissions(data.defaultRoleWisePermissions || [])

        if (editData) {
          // Edit mode — fill from existing record
          setSelectedNetId(editData.netId)
          setSelectedQueue(editData.queue)
          setSelectedRole(editData.role)
          setRolePermissions(editData.permissions || {})
          setInitialPermissions(editData.permissions || {})
        } else {
          // Add mode — fill defaults from role
          const defaultNet = data.netIds[0].netId || ""
          const defaultQueue = data.queues[0] || ""
          const defaultRole = data.roles[0] || ""
          setSelectedNetId(defaultNet)
          setSelectedQueue(defaultQueue)
          setSelectedRole(defaultRole)

          const defaultPerm =
            (
              data.defaultRoleWisePermissions.find(
                (r) => r.role === defaultRole
              ) || {}
            ).permissions || {}
          setRolePermissions(defaultPerm)
          setInitialPermissions(defaultPerm)
        }
      } catch {
        toast.error("Failed to load permission details")
      }
      setLoading(false)
    }

    loadDetails()
  }, [editData])

  const handleRoleChange = (role) => {
    setSelectedRole(role)
    const rolePerm =
      (defaultRoleWisePermissions.find((r) => r.role === role) || {})
        .permissions || {}
    setRolePermissions(rolePerm)
    setInitialPermissions(rolePerm)
  }

  // const handlePermissionToggle = (category, permKey) => {
  //   setRolePermissions((prev) => {
  //     const categoryPerms = new Set(prev[category] || [])
  //     categoryPerms.has(permKey)
  //       ? categoryPerms.delete(permKey)
  //       : categoryPerms.add(permKey)
  //     return { ...prev, [category]: Array.from(categoryPerms) }
  //   })
  // }

  const handlePermissionToggle = (category, permKey) => {
    setRolePermissions((prev) => {
      const updated = { ...prev }
      const categoryPerms = new Set(updated[category] || [])

      // Toggle the clicked permission
      if (categoryPerms.has(permKey)) {
        categoryPerms.delete(permKey)

        // If FOLDER_BROWSE is being unchecked, also remove related folder perms
        if (permKey === "FOLDER_BROWSE") {
          updated.DELETE = (updated.DELETE || []).filter(
            (p) => p !== "FOLDER_DELETE"
          )
          updated.WRITE = (updated.WRITE || []).filter(
            (p) => !["FOLDER_RENAME", "FOLDER_CREATE"].includes(p)
          )
        }
      } else {
        categoryPerms.add(permKey)
      }

      updated[category] = Array.from(categoryPerms)
      return updated
    })
  }

  const handleReset = () => {
    setRolePermissions(initialPermissions)
  }

  const handleSave = async () => {
    setActionLoading(true)
    try {
      let params = {
        role: selectedRole,
        netId: selectedNetId,
        emailAddress:
          netIds.find((v) => v.netId === selectedNetId)?.emailAddress || "",
        queue: selectedQueue,
        permissions: rolePermissions,
      }
      if (
        editData &&
        (editData.netId !== selectedNetId || editData.queue !== selectedQueue)
      ) {
        params = {
          ...params,
          oldNetId: editData.netId,
          oldQueue: editData.queue,
        }
      }

      await post("/manage/queue/netId/with-permissions", params)
      toast.success("Permission saved successfully")
      onSaved()
      setActionLoading(false)
    } catch (error) {
      toast.error(error, {
        duration: 5000,
      })
      setActionLoading(false)
    }
  }

  const optionNetIds = [
    ...new Map(
      netIds.map((r) => [r.netId, displayNetIdEmail(r.netId, r.emailAddress)])
    ).entries(),
  ]
    .map(([netId, label]) => ({
      value: netId,
      label: label,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

  const optionQueues = queues
    .map((n) => ({ value: n, label: n }))
    .sort((a, b) => a.value.localeCompare(b.value))
  const optionRoles = roles
    .map((n) => ({ value: n, label: n }))
    .sort((a, b) => a.value.localeCompare(b.value))

  return (
    <div className="checkbox-popup-overlay">
      <div className="checkbox-popup-modal">
        <div className="checkbox-popup-header">
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <h3 className="popup-title">
              {editData
                ? "Update Queue Allocation to NetId"
                : "Queue Allocation to NetId"}
            </h3>
            <span style={{ fontSize: "12px" }}>
              🛈 Assign role and permissions while allocating this queue to the
              selected NetId
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              fontSize: 24,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>

        {loading ? (
          <div className="loading-container-fax-list">
            <div className="loading-message">Loading...</div>
          </div>
        ) : (
          <>
            <div className="checkbox-popup-body">
              <div
                style={{
                  display: "flex",
                  gap: "20px", // space between each dropdown group
                  marginBottom: "10px",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                  }}
                >
                  <label htmlFor="selectNetId">Net ID:</label>

                  <Select
                    id="selectNetId"
                    className="basic-single"
                    classNamePrefix="select"
                    value={optionNetIds.find(
                      (option) => option.value === selectedNetId
                    )}
                    onChange={(selectedOption) =>
                      setSelectedNetId(selectedOption.value)
                    }
                    options={optionNetIds}
                    styles={customSelectStyles}
                    isSearchable
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                  }}
                >
                  <label>Queue:</label>

                  <Select
                    id="selectQueue"
                    className="basic-single"
                    classNamePrefix="select"
                    value={optionQueues.find(
                      (option) => option.value === selectedQueue
                    )}
                    onChange={(selectedOption) =>
                      setSelectedQueue(selectedOption.value)
                    }
                    options={optionQueues}
                    styles={customSelectStyles}
                    isSearchable
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                  }}
                >
                  <label>Role:</label>

                  <Select
                    id="selectRole"
                    className="basic-single"
                    classNamePrefix="select"
                    value={optionRoles.find(
                      (option) => option.value === selectedRole
                    )}
                    onChange={(selectedOption) => {
                      setSelectedRole(selectedOption.value)
                      handleRoleChange(selectedOption.value)
                    }}
                    options={optionRoles}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderRadius: 0,
                        minWidth: "200px",
                      }),
                    }}
                    // isSearchable
                  />
                </div>

                {/* <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setSelectedNetId(null)
                    setSelectedQueue(null)
                    setSelectedRole(null)
                  }}
                >
                  {" "}
                  Clear All
                </div> */}
              </div>

              {/* Permissions */}
              <div
                className={selectedRole === "OWNER" ? "locked" : ""}
                style={{
                  display: "flex",
                  gap: "10px",
                  marginTop: "30px",
                  flexWrap: "wrap",
                }}
              >
                {Object.entries(templates).map(([category, perms]) => (
                  <div
                    className="permission-box"
                    key={category}
                    style={{ flex: "1 1 200px" }}
                  >
                    <h3>{category}</h3>
                    {/* {Object.entries(perms).map(([permKey, permDesc]) => (
                      <div key={permKey}>
                        <label>
                          <input
                            type="checkbox"
                            checked={
                              rolePermissions[category]?.includes(permKey) ||
                              false
                            }
                            onChange={() =>
                              handlePermissionToggle(category, permKey)
                            }
                          />
                          {permDesc}
                        </label>
                      </div>
                    ))} */}

                    {Object.entries(perms).map(([permKey, permDesc]) => {
                      const folderBrowseChecked =
                        rolePermissions.READ?.includes("FOLDER_BROWSE")
                      const shouldDisable =
                        !folderBrowseChecked &&
                        [
                          "FOLDER_DELETE",
                          "FOLDER_RENAME",
                          "FOLDER_CREATE",
                        ].includes(permKey)

                      return (
                        <div key={permKey}>
                          <label className={shouldDisable ? "locked" : ""}>
                            <input
                              type="checkbox"
                              checked={
                                rolePermissions[category]?.includes(permKey) ||
                                false
                              }
                              onChange={() =>
                                handlePermissionToggle(category, permKey)
                              }
                              disabled={shouldDisable}
                            />
                            {permDesc}
                          </label>
                        </div>
                      )
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Action buttons */}
            <div className="checkbox-popup-footer popup-buttons">
              {" "}
              <button type="button" onClick={handleReset}>
                Reload default
              </button>
              <button
                className="popup-buttons cancel"
                type="button"
                onClick={onClose}
                style={{ marginLeft: "10px" }}
              >
                Cancel
              </button>
              <button
                className="popup-buttons save"
                type="button"
                onClick={handleSave}
                style={{ marginLeft: "10px" }}
              >
                {!actionLoading ? `Save` : `Saving ...`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
